import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { formatMaybeMultiValue } from '@/lib/crm/utils/formatMultiValue';
import { formatPhoneNumber } from '@/lib/crm/utils/phone';
import { formatRelativeTime } from '@/lib/utils/timeFormatting';
import type { CrmObject, PhoneNumber, QuantityValue } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';
import { formatCurrency } from '@/utils/currency';

import type { HighlightDefinition } from '../registry/entityTypeRegistry';
import { HighlightCardShell } from './HighlightCardShell';
import { iconForProperty } from './highlightIcons';
import { UpdateFieldModal } from './UpdateFieldModal';
import { useEditableHighlightField } from './useEditableHighlightField';

/**
 * Property names whose card click should copy the value to the clipboard.
 * Keeping this declarative keeps the card component side-effect-free for
 * properties where copy doesn't make semantic sense (e.g. job title).
 */
const COPYABLE_PROPERTIES = new Set<string>([
  'ahoy_email',
  'ahoy_phone_number',
  'ahoy_linkedin_url',
  'ahoy_domain',
]);

type PropertyDefinition = Extract<HighlightDefinition, { kind: 'property' }>;

interface Props {
  definition: PropertyDefinition;
  object: CrmObject;
  orgId: number;
}

function isQuantityLike(v: unknown): v is QuantityValue {
  return typeof v === 'object' && v !== null && 'amount' in v && 'unit' in v;
}

function isPhoneLike(v: object): v is PhoneNumber {
  return 'country_code' in v && 'national_number' in v;
}

function isDatePropertyName(propertyName: string): boolean {
  return (
    propertyName.endsWith('_date') ||
    propertyName.endsWith('_time') ||
    propertyName.endsWith('_at')
  );
}

function formatDateProperty(rawValue: string): string {
  const date = new Date(rawValue);
  const timestamp = date.getTime();
  if (isNaN(timestamp)) {
    return rawValue;
  }
  // Future dates are valid values (e.g., due dates, scheduled events); rendering
  // them via formatRelativeTime would surface "Just now" because that helper
  // clamps future timestamps as clock skew. Use an absolute date instead.
  if (timestamp > Date.now()) {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }
  const formatted = formatRelativeTime(rawValue);
  return formatted === 'Unknown time' ? rawValue : formatted;
}

/**
 * Format the property value for display. Returns `null` when the
 * property has no value — the shell renders an informative empty
 * state ("No <label>") rather than a generic em-dash.
 */
function formatRawValue(
  propertyName: string,
  rawValue: unknown
): string | null {
  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return null;
  }

  if (isQuantityLike(rawValue)) {
    const amount = Number(rawValue.amount);
    if (!isNaN(amount)) {
      return rawValue.unit
        ? formatCurrency(amount, rawValue.unit)
        : amount.toLocaleString();
    }
    return null;
  }

  if (typeof rawValue === 'boolean') {
    return rawValue ? 'Yes' : 'No';
  }

  if (isDatePropertyName(propertyName) && typeof rawValue === 'string') {
    return formatDateProperty(rawValue);
  }

  if (Array.isArray(rawValue)) {
    // formatMultiValue already routes phone-like objects through the canonical
    // formatter ("+1 415 …") and filters null/undefined entries before counting
    // overflow, so the generic multi-value path is correct for phone arrays too.
    return formatMaybeMultiValue(rawValue) || null;
  }

  if (typeof rawValue === 'object' && rawValue !== null) {
    if (isPhoneLike(rawValue)) {
      return formatPhoneNumber(rawValue);
    }
    // Unknown structured value — don't surface "[object Object]" to users.
    return null;
  }

  return String(rawValue);
}

export function PropertyHighlightCard({ definition, object, orgId }: Props) {
  const propValue = object.properties?.[definition.propertyName];
  const rawValue = getPropertyValue(propValue);

  const [isEditing, setIsEditing] = useState(false);
  const editableField = useEditableHighlightField(
    orgId,
    object.entity_type_name,
    'property',
    definition.propertyName
  );
  const handleEdit = useCallback(() => setIsEditing(true), []);
  const handleCloseEdit = useCallback(() => setIsEditing(false), []);

  const result = useMemo(
    () => ({
      value: formatRawValue(definition.propertyName, rawValue),
      isLoading: false as const,
      isError: false as const,
    }),
    [definition.propertyName, rawValue]
  );

  // Click-to-copy for known scalar identifiers (email, phone, etc.).
  // Other property types stay static — the value is informational, not
  // an action target. Async clipboard write with toast confirmation
  // mirrors the same pattern used elsewhere in the CRM UI (e.g.
  // EntityBadge's copy-id menu item).
  const isCopyable =
    COPYABLE_PROPERTIES.has(definition.propertyName) &&
    typeof result.value === 'string' &&
    result.value.length > 0;

  const handleCopy = useCallback(() => {
    if (typeof result.value !== 'string') {
      return;
    }
    // Guard against missing Clipboard API — in insecure contexts (http://) or
    // older browsers `navigator.clipboard` is undefined and reading `.writeText`
    // throws synchronously before the promise chain can catch it.
    if (!navigator.clipboard?.writeText) {
      toast.error('Could not copy to clipboard');
      return;
    }
    navigator.clipboard
      .writeText(result.value)
      .then(() =>
        toast.success(`Copied ${definition.label.toLowerCase()} to clipboard`)
      )
      .catch(() => toast.error('Could not copy to clipboard'));
  }, [result.value, definition.label]);

  return (
    <>
      <HighlightCardShell
        label={definition.label}
        result={result}
        icon={iconForProperty(definition.propertyName)}
        emptyText={`No ${definition.label.toLowerCase()}`}
        onClick={isCopyable ? handleCopy : null}
        actionLabel={
          isCopyable ? `Copy ${definition.label.toLowerCase()}` : undefined
        }
        onEdit={editableField ? handleEdit : undefined}
        editLabel={`Edit ${definition.label.toLowerCase()}`}
      />
      {isEditing && editableField && (
        <UpdateFieldModal
          isOpen
          onClose={handleCloseEdit}
          orgId={orgId}
          object={object}
          field={editableField}
        />
      )}
    </>
  );
}
